import type { IntelligenceAnalysis, ATSResult, AgentState } from '../../types/api';
import IntelligenceBrief from './IntelligenceBrief';
import HelpBubble from '../ui/HelpBubble';

interface ATSComparisonProps {
  analysis: IntelligenceAnalysis;
  atsResult: ATSResult;
  agents: AgentState[];
  candidateName: string;
  roleTitle?: string;
}

export default function ATSComparison({ analysis, atsResult, agents, candidateName, roleTitle }: ATSComparisonProps) {
  return (
    <div className="space-y-8">
      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: ATS Results — deliberately sterile */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
            <h2 className="text-sm font-bold text-neutral-500 uppercase tracking-widest">
              What Your ATS Would Show
            </h2>
            <HelpBubble
              content="72% of resumes are screened out before human review. This column shows what a keyword-matching ATS actually sees: pure text matching against a static document. No context, no nuance, no understanding of transferable skills. We're filtering human potential through algorithms that reward gaming over capability."
              source="Harvard Business Review (2016)"
            />
          </div>

          <div className="border border-neutral-700 rounded-lg bg-neutral-900 font-mono text-sm">
            {/* ATS Header */}
            <div className="px-6 py-4 border-b border-neutral-700 bg-neutral-800/50">
              <div className="text-neutral-400 text-xs uppercase tracking-widest mb-2">
                Applicant Tracking System
              </div>
              <div className="text-neutral-300 text-xs">
                Automated Resume Screening Report
              </div>
            </div>

            {/* Candidate info */}
            <div className="px-6 py-4 border-b border-neutral-700/50 space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-neutral-500">Candidate:</span>
                <span className="text-neutral-300">{candidateName}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-neutral-500">Position:</span>
                <span className="text-neutral-300">{roleTitle ?? 'Candidate Assessment'}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-neutral-500">Date Processed:</span>
                <span className="text-neutral-300">{new Date().toLocaleDateString()}</span>
              </div>
            </div>

            {/* Score */}
            <div className="px-6 py-4 border-b border-neutral-700/50">
              <div className="text-xs text-neutral-500 uppercase tracking-wider mb-2">
                Overall Score
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-neutral-300">
                  {atsResult.overall_score}
                </span>
                <span className="text-neutral-500 text-xs">/ 100</span>
              </div>
              <div className="mt-2 h-2 bg-neutral-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-neutral-500 rounded-full"
                  style={{ width: `${atsResult.overall_score}%` }}
                />
              </div>
            </div>

            {/* Keyword match */}
            <div className="px-6 py-4 border-b border-neutral-700/50">
              <div className="text-xs text-neutral-500 uppercase tracking-wider mb-3">
                Keyword Match ({atsResult.keywords_matched}/{atsResult.keywords_total})
              </div>
              <div className="space-y-1.5">
                {atsResult.keyword_details.map((kw, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-neutral-400">{kw.keyword}</span>
                    <span className={kw.found ? 'text-neutral-400' : 'text-neutral-600'}>
                      {kw.found ? '\u2713' : '\u2717'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Checks */}
            <div className="px-6 py-4 border-b border-neutral-700/50">
              <div className="text-xs text-neutral-500 uppercase tracking-wider mb-3">
                Screening Checks
              </div>
              <div className="space-y-2">
                {atsResult.checks.map((check, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-neutral-400">{check.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-500 text-[10px]">
                        {check.value}
                      </span>
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                          check.status === 'pass'
                            ? 'bg-neutral-700 text-neutral-400'
                            : check.status === 'fail'
                              ? 'bg-neutral-700 text-neutral-400'
                              : 'bg-neutral-700 text-neutral-500'
                        }`}
                      >
                        {check.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Disposition */}
            <div className="px-6 py-4">
              <div className="text-xs text-neutral-500 uppercase tracking-wider mb-2">
                Disposition
              </div>
              <div className="bg-neutral-800 border border-neutral-700 rounded px-4 py-3">
                <span className="text-xs text-neutral-400">
                  {atsResult.disposition}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: Intelligence Engine Results */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1.5 h-1.5 rounded-full bg-ti-green" />
            <h2 className="text-sm font-bold text-ti-green uppercase tracking-widest">
              Intelligence Engine Results
            </h2>
            <HelpBubble
              content="What if companies recruited like intelligence analysts? Instead of waiting for candidates to apply and keyword-matching their resumes, intelligence agents proactively gather structured data — track record, capabilities, cultural alignment — then synthesize it into actionable analysis. This transforms recruiting from a filtering problem to a matching one."
              source="From &quot;The Front Door is Broken&quot; — Contact Center Compass Issue 21"
            />
          </div>
          <IntelligenceBrief analysis={analysis} agents={agents} />
        </div>
      </div>

      {/* Comprehensive Summary Bar */}
      <div className="bg-ti-blue-dim/60 border border-ti-blue/20 rounded-xl px-6 py-6 md:px-8 md:py-7 space-y-5">
        {/* Main punchline - dynamic based on scores */}
        <div>
          <p className="text-sm md:text-base text-ti-text leading-relaxed">
            {analysis.match_score >= 85 ? (
              <>
                <span className="font-bold text-ti-blue">The intelligence engine found what keywords cannot measure.</span>{' '}
                {candidateName} scored {analysis.match_score}/100 on deep analysis while the ATS returned {atsResult.overall_score}/100 on keyword matching alone. {analysis.executive_summary.split('.').slice(0, 2).join('.')}.
              </>
            ) : analysis.match_score >= 65 ? (
              <>
                <span className="font-bold text-ti-blue">The ATS saw some matching keywords. The intelligence engine found a more nuanced picture.</span>{' '}
                {candidateName} presents an interesting case — the intelligence score of {analysis.match_score} tells a richer story than the ATS score of {atsResult.overall_score}. {analysis.executive_summary.split('.').slice(0, 2).join('.')}.
              </>
            ) : (
              <>
                <span className="font-bold text-ti-blue">The ATS and intelligence engine tell very different stories about this candidate.</span>{' '}
                {candidateName} scored {analysis.match_score}/100 on deep analysis and {atsResult.overall_score}/100 on the ATS. {analysis.executive_summary.split('.').slice(0, 2).join('.')}.
              </>
            )}
          </p>
        </div>

        {/* What ATS Would Miss */}
        <div>
          <h4 className="text-xs font-semibold text-ti-blue uppercase tracking-wider mb-3 flex items-center gap-2">
            <svg className="w-4 h-4 text-ti-amber" viewBox="0 0 16 16" fill="none">
              <path d="M8 1L10 6H15L11 9.5L12.5 15L8 11.5L3.5 15L5 9.5L1 6H6L8 1Z" stroke="currentColor" strokeWidth="1" fill="currentColor" fillOpacity="0.3" />
            </svg>
            What ATS Systems Cannot Evaluate
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {analysis.ats_would_miss.map((item, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-ti-text">
                <span className="w-1 h-1 rounded-full bg-ti-amber mt-2 shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
